import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck } from "@phosphor-icons/react";
import { toast } from "sonner";

/** FulfilMRDialog — pick source warehouse and confirm fulfilment.
 * Backend auto-creates a Stock Transfer and moves the goods.
 */
export default function FulfilMRDialog({ mr, onClose, onDone }) {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/warehouses");
        const active = (data || []).filter((w) => w.active !== false);
        setWarehouses(active);
        if (active.length === 1) setWarehouseId(active[0].id);
      } catch (e) { toast.error("Failed to load warehouses"); }
    })();
  }, []);

  const fulfil = async () => {
    if (!warehouseId) return toast.error("Pick the source warehouse");
    setBusy(true);
    try {
      await api.post(`/material-requests/${mr.id}/fulfil`, { warehouse_id: warehouseId });
      toast.success(`${mr.mr_no} fulfilled · stock transferred to site`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck size={16} className="text-[#EA580C]" weight="fill" /> Fulfil {mr.mr_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-[12.5px] text-slate-600">
            Choose the source warehouse. We&apos;ll decrement stock there and create a Stock Transfer
            that lands the goods on this project&apos;s site inventory. If any line lacks enough
            stock, nothing is moved — fix the shortage first.
          </p>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Source warehouse</label>
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger data-testid="fulfil-warehouse-select"><SelectValue placeholder="Pick warehouse…" /></SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name} · {w.code}</SelectItem>)}
              </SelectContent>
            </Select>
            {warehouses.length === 0 && (
              <div className="mt-2 text-[11.5px] bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1.5 rounded-sm">
                No warehouses configured. Please add one under Procurement → Warehouses first.
              </div>
            )}
          </div>
          <div className="border border-slate-200 rounded-sm p-2 bg-slate-50 text-[11.5px] max-h-40 overflow-y-auto">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Items to issue</div>
            {(mr.items || []).map((it, i) => (
              <div key={`${it.sku || ""}-${it.description || ""}-${i}`} className="flex justify-between py-0.5">
                <span className="truncate">{it.description}{it.sku && <span className="text-slate-400 ml-1 font-mono">({it.sku})</span>}</span>
                <span className="font-mono-num text-slate-700">{it.quantity} {it.unit}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button
              onClick={fulfil}
              disabled={busy || !warehouseId}
              data-testid="fulfil-confirm-btn"
              className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
            >
              <Truck size={14} className="mr-2" />
              {busy ? "Issuing…" : "Fulfil & transfer stock"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
